import { Link } from '@mui/material'
import { SummaryTableRow } from 'components/SummaryTable'
import {
  DiscoverySummary,
  LeafUploadResult,
  NodeUploadResult,
  StageStatus,
  UploadSummary,
  UrlResult,
} from './types'

function getNumberByKeys(
  source: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback = 0,
): number {
  if (!source) return fallback

  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'number') {
      return value
    }
  }

  return fallback
}

function getTextValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  return String(value)
}

function getUrlTextValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  return String(value)
}

function isHttpUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://')
}

function getUrlCell(value: unknown): React.ReactNode {
  const url = getUrlTextValue(value)
  if (!url) {
    return '-'
  }

  if (!isHttpUrl(url)) {
    return (
      <span
        title={url}
        style={{
          display: 'inline-block',
          maxWidth: 360,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'bottom',
        }}
      >
        {url}
      </span>
    )
  }

  return (
    <Link
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      title={url}
      underline="hover"
      sx={{
        display: 'inline-block',
        maxWidth: 360,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'bottom',
      }}
    >
      {url}
    </Link>
  )
}

function getRecordByKeys(
  source: Record<string, unknown> | null | undefined,
  keys: string[],
): Record<string, number> | null {
  if (!source) return null

  for (const key of keys) {
    const value = source[key]
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.values(value).every((entry) => typeof entry === 'number')
    ) {
      return value as Record<string, number>
    }
  }

  return null
}

function formatHostCounts(
  hostCounts: Record<string, number> | null,
): string | React.ReactNode {
  if (!hostCounts || Object.keys(hostCounts).length === 0) {
    return '-'
  }

  const lines = Object.entries(hostCounts)
    .sort(([hostA], [hostB]) => hostA.localeCompare(hostB))
    .map(([host, count]) => `${host} (${count})`)

  return (
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
      }}
    >
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

function getNestedOrSelfResults(
  result: NodeUploadResult | LeafUploadResult,
): UrlResult[] {
  if (Array.isArray(result.url_results) && result.url_results.length > 0) {
    return result.url_results
  }

  return [result]
}

function getStatusCell(
  status: string,
  error: string | null,
): string | React.ReactNode {
  const normalized = status.toLowerCase()
  const isFailed = normalized.includes('fail') || Boolean(error)
  const label = error ? `${status}: ${error}` : status

  if (!isFailed) {
    return label
  }

  return <span style={{ color: '#d32f2f' }}>{label}</span>
}

/**
 * Builds summary rows for upload stage status.
 *
 * @param status - Current upload stage status.
 * @returns Summary table rows.
 */
export function getStatusTableRows(status: StageStatus): SummaryTableRow[] {
  const completedJobs =
    status.dependant_jobs.completed +
    status.worker_jobs.completed +
    status.dependant_jobs.failed +
    status.worker_jobs.failed
  const totalJobs = status.dependant_jobs.total + status.worker_jobs.total
  const failedJobs = status.dependant_jobs.failed + status.worker_jobs.failed

  return [
    {
      cells: ['Status', status.completed ? 'Completed' : 'In Progress'],
    },
    {
      cells: ['Jobs', `${completedJobs}/${totalJobs}`],
    },
    {
      cells: ['Failed Jobs', failedJobs],
    },
  ]
}

/**
 * Builds summary rows from discovery data.
 *
 * @param discovery - Discovery summary payload.
 * @returns Summary table rows.
 */
export function getDiscoverySummaryRows(
  discovery: DiscoverySummary | null | undefined,
): SummaryTableRow[] {
  const nodeMatches = getNumberByKeys(
    discovery,
    ['node_matches', 'node_match_count'],
    0,
  )
  const leafMatches = getNumberByKeys(
    discovery,
    ['leaf_matches', 'leaf_match_count'],
    0,
  )
  const totalJobs = getNumberByKeys(
    discovery,
    ['total_jobs', 'total_upload_jobs'],
    0,
  )
  const hostCounts =
    getRecordByKeys(discovery, ['host_counts']) ||
    getRecordByKeys(discovery, ['node_host_counts']) ||
    getRecordByKeys(discovery, ['leaf_host_counts'])

  return [
    {
      cells: ['Node Matches', nodeMatches],
    },
    {
      cells: ['Leaf Matches', leafMatches],
    },
    {
      cells: ['Total Jobs', totalJobs],
    },
    {
      cells: ['Host Counts', formatHostCounts(hostCounts)],
    },
  ]
}

/**
 * Builds summary rows from upload results summary.
 *
 * @param summary - Upload summary payload.
 * @returns Summary table rows.
 */
export function getUploadSummaryRows(
  summary: UploadSummary | null | undefined,
): SummaryTableRow[] {
  const successful = getNumberByKeys(
    summary,
    ['successful_uploads', 'successful'],
    0,
  )
  const failed = getNumberByKeys(summary, ['failed_uploads', 'failed'], 0)
  const total = getNumberByKeys(
    summary,
    ['total_urls', 'total'],
    successful + failed,
  )

  return [
    {
      cells: ['Total URLs', total],
    },
    {
      cells: ['Successful Uploads', successful],
    },
    {
      cells: ['Failed Uploads', failed],
    },
  ]
}

/**
 * Builds per-URL result rows across node and leaf upload results.
 *
 * @param nodeResults - Node-level upload results.
 * @param leafResults - Leaf-level upload results.
 * @returns Summary table rows.
 */
export function getUrlResultRows(
  nodeResults: NodeUploadResult[] | null | undefined,
  leafResults: LeafUploadResult[] | null | undefined,
): SummaryTableRow[] {
  const rows: SummaryTableRow[] = []

  ;(nodeResults || []).forEach((result) => {
    const nestedResults = getNestedOrSelfResults(result)

    nestedResults.forEach((urlResult) => {
      const status =
        typeof urlResult.status === 'string' && urlResult.status.length > 0
          ? urlResult.status
          : typeof urlResult.success === 'boolean'
            ? urlResult.success
              ? 'success'
              : 'failed'
            : 'unknown'
      const error =
        typeof urlResult.error === 'string' && urlResult.error.length > 0
          ? urlResult.error
          : null

      rows.push({
        cells: [
          getTextValue(urlResult.type || 'Node'),
          getTextValue(urlResult.id || result.node_id),
          getTextValue(urlResult.field || urlResult.field_name),
          getStatusCell(status, error),
          getUrlCell(urlResult.previous_url || urlResult.original_url),
          getUrlCell(urlResult.new_url || urlResult.uploaded_url),
        ],
      })
    })
  })
  ;(leafResults || []).forEach((result) => {
    const nestedResults = getNestedOrSelfResults(result)

    nestedResults.forEach((urlResult) => {
      const status =
        typeof urlResult.status === 'string' && urlResult.status.length > 0
          ? urlResult.status
          : typeof urlResult.success === 'boolean'
            ? urlResult.success
              ? 'success'
              : 'failed'
            : 'unknown'
      const error =
        typeof urlResult.error === 'string' && urlResult.error.length > 0
          ? urlResult.error
          : null

      rows.push({
        cells: [
          getTextValue(urlResult.type || 'Leaf'),
          getTextValue(urlResult.id || result.leaf_id),
          getTextValue(urlResult.field || urlResult.field_name),
          getStatusCell(status, error),
          getUrlCell(urlResult.previous_url || urlResult.original_url),
          getUrlCell(urlResult.new_url || urlResult.uploaded_url),
        ],
      })
    })
  })

  return rows
}
